import { Component, ElementRef, computed, inject, input, viewChild } from '@angular/core'

interface FileIconConfiguration {
  readonly colorClass: string
  readonly extensionIcon: string
}

@Component({
  selector: 'aw-file-icon',
  templateUrl: './file-icon.component.html',
  styleUrls: ['./file-icon.component.scss'],
  imports: [],
  host: {
    '[class]': 'size()'
  }
})
export class FileIconComponent {
  element = inject(ElementRef)

  readonly extension = input<string>()
  readonly name = input<string>()
  readonly mimeType = input<string>('')
  readonly size = input<'xxxsmall' | 'xxsmall' | 'xsmall' | 'small' | 'normal' | 'big' | 'row'>('normal')

  readonly fileExtension = viewChild<ElementRef>('fileExtension')

  readonly normalizedExtension = computed(() => this.getNormalizedExtension())
  readonly iconConfiguration = computed(() => this.getIconConfiguration(this.normalizedExtension()))

  /**
   * Returns the file extension in lowercase. If the extension is not provided, it tries to extract it from the file name.
   * @returns {string}
   */
  private getNormalizedExtension(): string {
    if (!this.extension() && this.name()) {
      const regExp = /(?:\.([^.]+))?$/

      return regExp.exec(this.name())?.[1]?.toLowerCase()
    }

    return this.extension()?.toLowerCase()
  }

  /**
   * Determines the icon and color based on the file extension.
   * @param {string} extension
   * @returns {FileIconConfiguration} The icon configuration for the extension.
   */
  private getIconConfiguration(extension: string): FileIconConfiguration {
    let colorClass = ''
    let extensionIcon = ''

    if (['doc', 'docx', 'document'].includes(extension)) {
      colorClass = 'blue'
      extensionIcon = 'W'
    } else if (['pdf'].includes(extension)) {
      colorClass = 'red'
      extensionIcon = 'PDF'
    } else if (
      ['bmp', 'gif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'heic', 'eps', 'raw', 'webp'].includes(
        extension
      )
    ) {
      colorClass = 'royalBlue'
    } else if (['sketch'].includes(extension)) {
      colorClass = 'yellow'
    } else if (['pps', 'ppt', 'pptx', 'presentation'].includes(extension)) {
      colorClass = 'orange'
      extensionIcon = 'P'
    } else if (['aif', 'cda', 'mid', 'midi', 'mp3', 'mpa', 'ogg', 'wav', 'wma', 'wp'].includes(extension)) {
      colorClass = 'lightGreen'
    } else if (['xls', 'xls', 'xlsx', 'spreadsheet'].includes(extension)) {
      colorClass = 'green'
      extensionIcon = 'X'
    } else if (['csv'].includes(extension)) {
      colorClass = 'green'
    } else if (
      ['avi', 'flv', 'h264', 'm4v', 'mkv', 'mov', 'mp4', 'mpg', 'mpeg', 'rm', 'swf', 'vob', 'wmv', 'webm'].includes(
        extension
      )
    ) {
      colorClass = 'pink'
    } else if (['zip'].includes(extension)) {
      colorClass = 'deepYellow'
      extensionIcon = 'ZIP'
    } else if (['rar'].includes(extension)) {
      colorClass = 'purple'
      extensionIcon = 'RAR'
    } else if (['ai'].includes(extension)) {
      colorClass = 'deepOrange'
      extensionIcon = 'Ai'
    } else if (['indd'].includes(extension)) {
      colorClass = 'deepPink'
      extensionIcon = 'In'
    } else if (['aep'].includes(extension)) {
      colorClass = 'deepBlue'
      extensionIcon = 'Ae'
    } else if (['prproj'].includes(extension)) {
      colorClass = 'deepBlue'
      extensionIcon = 'Pr'
    } else if (['psd', 'ps'].includes(extension)) {
      colorClass = 'lightBlue'
      extensionIcon = 'Ps'
    } else if (['xd'].includes(extension)) {
      colorClass = 'lightPink'
      extensionIcon = 'Xd'
    }

    return { colorClass, extensionIcon }
  }
}
